<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import FilterWhenField from './service-models/FilterWhenField.vue'
import FormAlert from '@/components/FormAlert.vue'
import { filterPopoverPosition, POPOVER_WIDTH } from '../utils/filterPopoverPosition'
import type { ScopeDescriptor } from '@/types/bbel-types'

// Filter when, edited on the canvas. It holds the same field the editor's Advanced settings hold. The edit stays
// in this popover until Save; closing it any other way drops the edit, after asking when the text was changed.
const props = defineProps<{
  /** The service's saved filter. */
  expression: string
  scope: ScopeDescriptor
  /** The funnel icon's box on screen at the moment it was clicked. */
  anchor: DOMRect
  /** Save-mutation error, rendered the way the service panel renders it. */
  saveError?: unknown
  /** A save is in flight. The popover cannot close until it settles, so its outcome lands on this popover. */
  saving: boolean
}>()

const emit = defineEmits<{
  save: [expression: string]
  close: []
  'dismiss-error': []
}>()

const editedExpression = ref(props.expression)
const isUnchanged = computed(() => editedExpression.value === props.expression)

// Below Bootstrap's sm breakpoint there is no room beside the icon, so the popover is a sheet along the bottom.
const isSheet = window.matchMedia('(max-width: 575.98px)').matches

// The card's height is measured so the position can keep its bottom edge in view. It changes when the save
// error appears.
const card = ref<HTMLElement | null>(null)
const cardHeight = ref(0)
const cardResize = new ResizeObserver(([entry]) => {
  cardHeight.value = (entry.target as HTMLElement).offsetHeight
})

const besideIcon = computed(() => {
  const viewport = { width: window.innerWidth, height: window.innerHeight }
  const { left, top, maxHeight } = filterPopoverPosition(props.anchor, viewport, cardHeight.value)
  return { left: `${left}px`, top: `${top}px`, width: `${POPOVER_WIDTH}px`, maxHeight: `${maxHeight}px` }
})

// Focus moves into the popover so Escape reaches it without a click first, and goes back to the element that
// had it (the funnel icon) when the popover goes away.
const focusedBeforeOpen = document.activeElement instanceof HTMLElement ? document.activeElement : null
onMounted(() => {
  const cardElement = card.value as HTMLElement
  cardResize.observe(cardElement)
  cardElement.focus()
})
onBeforeUnmount(() => {
  cardResize.disconnect()
  focusedBeforeOpen?.focus()
})

// Every dismissal comes here: the backdrop, Escape and Close. It asks before dropping a changed filter, with the
// service panel's question (BuilderView.vue handleServicePanelClose).
function close() {
  if (props.saving) return
  if (!isUnchanged.value && !confirm('Discard unsaved changes?')) return
  emit('close')
}

// Escape closes the popover, unless the expression field already used it to close its suggestion list
// (CodeMirror marks the event handled before it bubbles here).
function onEscape(event: KeyboardEvent) {
  if (event.defaultPrevented) return
  close()
}
</script>

<template>
  <!-- Teleported for the reason the service panel is: a transformed canvas ancestor breaks position: fixed. -->
  <Teleport to="body">
    <!-- The backdrop covers the canvas. A press on it closes the popover, and while it is up the canvas cannot
         pan, so the place the popover was given stays right. -->
    <div class="position-fixed top-0 start-0 w-100 h-100 filter-popover-backdrop" @mousedown.self="close">
      <div
        ref="card"
        class="card shadow position-fixed"
        :class="{ 'bottom-0 start-0 end-0 rounded-top-4 rounded-bottom-0': isSheet }"
        :style="isSheet ? undefined : besideIcon"
        role="dialog"
        aria-modal="true"
        aria-label="Filter when"
        tabindex="-1"
        @keydown.esc="onEscape"
      >
        <!-- The body scrolls when the card is capped to the viewport; the buttons stay in view below it. -->
        <div class="card-body p-3 overflow-auto">
          <FormAlert :error="saveError" @dismiss="emit('dismiss-error')" />
          <FilterWhenField v-model="editedExpression" :scope="scope" />
        </div>
        <div class="d-flex justify-content-end gap-2 p-3 pt-0">
          <button
            type="button"
            class="bb-btn-primary-cta btn-sm"
            :disabled="isUnchanged || saving"
            @click="emit('save', editedExpression)"
          >
            <i class="bi bi-save me-1"></i>
            Save
          </button>
          <button type="button" class="bb-btn-cancel" :disabled="saving" @click="close">Close</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* Over the canvas and its toolbars. Under Bootstrap's modal (1055), so the expression field's fullscreen
   editor opens over the popover. */
.filter-popover-backdrop {
  z-index: 1050;
}
</style>
